import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Ship, Users, TrendingUp, Target } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const WarehouseAIArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-black text-white">
                <Ship className="h-3 w-3 mr-1" />
                {t('article.warehouseai.badge')}
              </Badge>
              <span className="text-gray-500">{t('article.warehouseai.date')}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.warehouseai.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.warehouseai.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://imgs.newseditor.aws.kuehne-nagel.cloud/2b1af638-1cb1-47ea-a4b3-15d6eb3dfdac.jpg"
              alt="Supply chain diversification and business strategy" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Target className="h-5 w-5 mr-2 text-black" />
                  {t('warehouseai.stats.participation.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-black">{t('warehouseai.stats.participation.value')}</div>
                <p className="text-sm text-gray-600">{t('warehouseai.stats.participation.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Users className="h-5 w-5 mr-2 text-purple-600" />
                  {t('warehouseai.stats.trust.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-purple-600">{t('warehouseai.stats.trust.value')}</div>
                <p className="text-sm text-gray-600">{t('warehouseai.stats.trust.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                  {t('warehouseai.stats.growth.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('warehouseai.stats.growth.value')}</div>
                <p className="text-sm text-gray-600">{t('warehouseai.stats.growth.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('warehouseai.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('warehouseai.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('warehouseai.section2.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('warehouseai.section2.content')}
            </p>
            <div className="bg-purple-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('warehouseai.section2.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('warehouseai.section2.list')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('warehouseai.section3.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('warehouseai.section3.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('warehouseai.section3.list')}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('warehouseai.section4.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('warehouseai.section4.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('warehouseai.section4.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('warehouseai.section5.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('warehouseai.section5.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('warehouseai.section6.title')}</h3>
            <div className="bg-gray-100 p-6 rounded-lg mb-6 border-l-4 border-black">
              <blockquote className="text-gray-700 italic mb-4">
                {t('warehouseai.section6.quote')}
              </blockquote>
              <p className="text-sm font-semibold text-gray-900">
                — {t('warehouseai.section6.author')}
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('warehouseai.section7.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('warehouseai.section7.content')}
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('warehouseai.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://mykn.kuehne-nagel.com/news/article/red-sea-reopenings-diverge-cma-cgm-advances', '_blank')}
              >
                View Original Source
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default WarehouseAIArticle;